import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Gift, ArrowRight, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LeadMagnetDialog = ({ open, onOpenChange, source = "free_session" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await api.post("/leads", { ...form, source });
      setSuccess(data);
    } catch {
      toast.error("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "" });
    setSuccess(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="bg-spike-elevated border border-spike-blue/40 text-spike-text max-w-md p-0"
        data-testid="lead-magnet-dialog"
      >
        {!success ? (
          <>
            <div className="relative bg-spike-elevated/60 p-7 border-b border-white/10">
              <Gift className="w-7 h-7 text-spike-blue-light" strokeWidth={1.5} />
              <DialogHeader className="mt-4 text-left">
                <DialogTitle className="font-orbitron font-black uppercase text-2xl text-spike-text tracking-tight">
                  Try a session — <span className="text-spike-blue-light">on us.</span>
                </DialogTitle>
                <DialogDescription className="font-exo text-spike-text-muted text-sm mt-2">
                  Drop your details and we'll send a one-time free Drop-In credit
                  for Mississauga. No card, no commitment.
                </DialogDescription>
              </DialogHeader>
            </div>
            <form onSubmit={submit} className="p-7 space-y-4" data-testid="lead-magnet-form">
              <div>
                <label className="block text-[0.65rem] font-bold uppercase tracking-[0.22em] text-spike-text-muted mb-2 font-orbitron">Full name</label>
                <input
                  type="text"
                  data-testid="lead-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text placeholder:text-spike-dim focus:border-spike-blue focus:outline-none font-exo"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-[0.65rem] font-bold uppercase tracking-[0.22em] text-spike-text-muted mb-2 font-orbitron">Email</label>
                <input
                  type="email"
                  data-testid="lead-input-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text placeholder:text-spike-dim focus:border-spike-blue focus:outline-none font-exo"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="block text-[0.65rem] font-bold uppercase tracking-[0.22em] text-spike-text-muted mb-2 font-orbitron">Phone (optional)</label>
                <input
                  type="tel"
                  data-testid="lead-input-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-spike-elevated border border-white/10 px-4 py-3 text-spike-text placeholder:text-spike-dim focus:border-spike-blue focus:outline-none font-exo"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="w-full mt-2 inline-flex items-center justify-center gap-3 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-4 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Generating credit...
                  </>
                ) : (
                  <>
                    Claim free session <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="p-8 text-center" data-testid="lead-magnet-success">
            <div className="w-14 h-14 mx-auto bg-spike-blue/15 border border-spike-blue/40 flex items-center justify-center mb-5">
              <Check className="w-7 h-7 text-spike-blue-light" strokeWidth={2} />
            </div>
            <DialogTitle className="font-orbitron font-black uppercase text-2xl text-spike-text">
              You're in.
            </DialogTitle>
            <p className="mt-3 font-exo text-spike-text-muted text-sm">
              Show this code at the Mississauga front desk:
            </p>
            <div
              data-testid="lead-magnet-code"
              className="mt-6 inline-block bg-spike-elevated border border-spike-blue px-6 py-4 font-orbitron font-black text-2xl tracking-[0.3em] text-spike-blue-light"
            >
              {success.code}
            </div>
            <p className="mt-6 text-xs font-exo text-spike-dim leading-relaxed">
              We've also emailed it to <span className="text-spike-text">{success.email}</span>.
              Code valid for 30 days.
            </p>
            <button
              type="button"
              data-testid="lead-magnet-close"
              onClick={() => onOpenChange(false)}
              className="mt-6 inline-flex items-center justify-center gap-2 border border-white/10 hover:border-spike-blue text-spike-text-muted hover:text-spike-text font-orbitron uppercase tracking-[0.22em] text-[0.7rem] px-5 py-3 transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadMagnetDialog;
